import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from '$store';
import {
  addRelation,
  commitUploadOfRelatedFile,
  getAssetDuplicates,
  getRelationList,
  getUploadTicket,
  removeRelation,
  sendFile,
} from '~modules/media-library/controller/api/assets';

export const selectState = (state: RootState) => state.assetRelations;

export interface RelationFile {
  assetId: string;
  relationId: number;
  relationType: string;
  relatedAssetId: number;
}

// fetch relation list
export const fetchRelationList = createAsyncThunk(
  'assets/getRelationList',
  async (assetId: string, { rejectWithValue }) => {
    try {
      const response = getRelationList(assetId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// delete asset relation
export const removeRelationOfAsset = createAsyncThunk(
  'assets/getRelationList',
  async (file: RelationFile, { rejectWithValue, dispatch }) => {
    try {
      const response = await removeRelation(file);
      dispatch(fetchRelationList(file.assetId));
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// multi file upload
export const upload = createAsyncThunk(
  'upload/uploadFile',
  async (
    { files, mainAssetId }: { files: { file: File; hash: string }[]; mainAssetId: string },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;

    dispatch(setProcessInProgress(true));
    dispatch(setUploadInProgress(true));

    try {
      const { selectedDocumentType } = state.assetRelations;

      // Add all files to the state with their fileName and fileSize before starting the upload
      files.forEach(({ file }) => {
        dispatch(
          addUploadedFile({
            fileName: file.name,
            fileSize: file.size,
          }),
        );
      });

      let completedUploads = 0;

      // Iterate over each file and upload it
      for (const [index, { file, hash }] of files.entries()) {
        dispatch(setFile(file));

        // get duplicates using the corresponding hash
        const duplicateAssetId: string[] = await getAssetDuplicates(hash, [selectedDocumentType]);

        if (duplicateAssetId.length === 0) {
          const ticketRes = (await getUploadTicket()) as { key: string; url: string };

          try {
            await sendFile(file, ticketRes?.url, progress => {
              dispatch(updateFileProgress({ fileName: file.name, progress, index }));
            });
          } catch (e: any) {
            const errorMessage = e.error || (e instanceof Error ? e.message : 'Unknown error');
            dispatch(
              updateUploadedFile({
                fileName: file.name,
                error: true,
                errorMessage: errorMessage,
              }),
            );
          }

          try {
            await commitUploadOfRelatedFile(ticketRes?.key, file.name, selectedDocumentType, mainAssetId);
          } catch (e: any) {
            const errorMessage = e.error || (e instanceof Error ? e.message : 'Unknown error');
            dispatch(
              updateUploadedFile({
                fileName: file.name,
                error: true,
                errorMessage: errorMessage,
              }),
            );
          }
        } else {
          await addRelation(selectedDocumentType, mainAssetId, duplicateAssetId[0]);
        }

        completedUploads++;

        if (completedUploads === files.length) {
          dispatch(fetchRelationList(mainAssetId));
          dispatch(setUploadInProgress(false));
          dispatch(setSelectedDocumentType(null));
        }
      }
    } catch (e) {
      dispatch(setProcessInProgress(false));
      console.error('Error during file upload:', e);
      throw e;
    }
  },
);

const resetStateFields = (state: any) => {
  state.selectedDocumentType = '';
  state.allFiles = [];
};

const AssetRelationsSlice = createSlice({
  name: 'AssetRelationsSlice',
  initialState: {
    uploadInProgress: false,
    selectedDocumentType: '',
    allFiles: [],
    isProcessInProgress: false,
    file: {
      name: '',
      size: ' ',
    },
    relevantDocuments: [],
    relevantDocumentsLoading: false,
  },
  extraReducers: builder => {
    builder.addCase(fetchRelationList.pending, state => {
      state.relevantDocumentsLoading = true;
    });
    builder.addCase(fetchRelationList.fulfilled, (state, action: any) => {
      const { list } = action.payload;
      state.relevantDocuments = list;
      state.allFiles = [];
      state.relevantDocumentsLoading = false;
    });
  },
  reducers: {
    setUploadInProgress: (state, action) => {
      state.uploadInProgress = action.payload;
    },

    resetState: state => {
      resetStateFields(state);
    },
    setSelectedDocumentType: (state, action) => {
      state.selectedDocumentType = action.payload;
    },
    setProcessInProgress: (state, action) => {
      state.isProcessInProgress = action.payload;
    },
    setFile: (state, action) => {
      state.file = action.payload;
    },
    updateFileProgress: (state, action) => {
      const { fileName, progress } = action.payload;
      const file = state.allFiles.find(file => file.fileName === fileName);
      if (file) {
        file.progress = progress;
      }
    },
    updateUploadedFile: (state, action) => {
      const { fileName, contributionType, id, errorMessage } = action.payload;
      const file = state.allFiles.find(file => file.fileName === fileName);
      if (file) {
        file.contributionType = contributionType;
        file.id = id;
        file.errorMessage = errorMessage;
      }
    },
    addUploadedFile: (state, action) => {
      state.allFiles.push(action.payload);
    },
  },
});

export const {
  resetState,
  setSelectedDocumentType,
  setProcessInProgress,
  setFile,
  updateFileProgress,
  updateUploadedFile,
  addUploadedFile,
  setUploadInProgress,
} = AssetRelationsSlice.actions;

export default AssetRelationsSlice.reducer;
